import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Calculator,
  ClipboardCheck,
  FileCheck2,
  FileText,
  GaugeCircle,
  Inbox,
  ListChecks,
  PackageOpen,
  PackageSearch,
  Plus,
  Send,
  Sparkles,
  Truck,
  Undo2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useAnalysePolicyBatch, useProcurementDashboard } from "@/api/queries";
import type { ProcurementOverview } from "@/api/types";
import { Card, CardHeader } from "@/components/Card";
import { ErrorState, Skeleton } from "@/components/States";
import { useAuth, useCanWrite } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { useToast } from "@/components/toast-context";
import { cn } from "@/lib/cn";
import {
  formatDate,
  formatEstimateVersionStatus,
  formatInteger,
  formatMaterialListStatus,
  formatPurchaseOrderStatus,
  formatQuantityWithUnit,
  formatRelative,
  formatRemainderReason,
  formatRfqStatus,
  formatSupplierReturnStatus,
  formatSurplusStatus,
  pluralize,
} from "@/lib/format";
import { formatPlanStatus } from "@/lib/plan-display";
import { formatQuoteStatus } from "@/lib/quote-display";

/**
 * L'approvisionnement sur le dashboard (V1.6-DASHBOARD).
 *
 * Sept modules livrés en V1.6-01 à V1.6-05 qui n'apparaissaient nulle part sur
 * l'écran d'accueil : on ne les refait pas, on les rend visibles.
 *
 * Trois règles tenues ici :
 *
 * 1. **Aucun chiffre inventé.** Tout vient de `GET /dashboard/procurement`, en
 *    un seul appel. Zéro est affiché quand la donnée dit zéro, jamais pour
 *    remplir une carte ;
 * 2. **`FAILED` et `UNKNOWN_DELIVERY` ne sont pas « envoyé ».** Trois lignes
 *    distinctes, et la livraison inconnue porte son nom : le fournisseur l'a
 *    peut-être reçu, et c'est exactement ce qu'il faut dire ;
 * 3. **une carte vide propose l'action, pas le vide.** « Aucune liste active »
 *    avec le bouton qui suit vaut mieux qu'un écran qui a l'air cassé.
 *
 * Un module que l'API ne sert pas n'a **pas** de carte : `available_modules`
 * décide. « 0 soumission » ferait croire qu'on en attend une alors que le
 * module n'existe pas.
 *
 * La carte des plans d'achat (V1.6-08) ne compte **pas** les plans périmés :
 * le dire demanderait une comparaison complète par plan, et un écran d'accueil
 * ne peut pas payer ça. La péremption se lit sur la fiche du plan, où elle est
 * exacte et où elle sert à décider.
 */
export function ProcurementSection() {
  const { data, isLoading, error, refetch } = useProcurementDashboard();
  const canWrite = useCanWrite();

  const has = (name: string) => data?.available_modules.includes(name) ?? false;

  return (
    <section className="mt-6 min-w-0" aria-labelledby="procurement-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="procurement-heading" className="text-[15px] font-semibold text-ink">
          Approvisionnement
        </h2>
        {data && (
          <p className="text-[11px] text-ink-subtle">
            Lu {formatRelative(data.generated_at)}
          </p>
        )}
      </div>

      {error && (
        <Card className="mt-3">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </Card>
      )}

      {/* Une grille qui monte à trois colonnes seulement quand il y a la place.
          Sur téléphone, une carte par ligne : sept cartes compressées à 390 px
          ne sont pas un tableau de bord, c'est une grille desktop illisible. */}
      <div className="mt-3 grid min-w-0 gap-3 @3xl:grid-cols-2 @6xl:grid-cols-3">
        {(isLoading || has("material_lists")) && (
          <ListsCard data={data} loading={isLoading} canWrite={canWrite} />
        )}
        {(isLoading || has("project_estimates")) && (
          <EstimatesCard data={data} loading={isLoading} canWrite={canWrite} />
        )}
        {(isLoading || has("project_requirements")) && (
          <RequirementsCard data={data} loading={isLoading} />
        )}
        {(isLoading || has("inventory_policies")) && (
          <ReplenishmentCard data={data} loading={isLoading} />
        )}
        {(isLoading || has("rfqs")) && (
          <RfqCard data={data} loading={isLoading} canWrite={canWrite} />
        )}
        {(isLoading || has("supplier_quotes")) && (
          <QuoteCard data={data} loading={isLoading} />
        )}
        {(isLoading || has("purchase_plans")) && <PlanCard data={data} loading={isLoading} />}
        {(isLoading || has("purchase_orders")) && <OrderCard data={data} loading={isLoading} />}
        {(isLoading || has("supplier_returns")) && <ReturnCard data={data} loading={isLoading} />}
        {(isLoading || has("project_surpluses")) && (
          <SurplusCard data={data} loading={isLoading} />
        )}
        {(isLoading || has("outbound_messages")) && (
          <OutboundCard data={data} loading={isLoading} />
        )}
        {(isLoading || has("inbound_messages")) && <InboundCard data={data} loading={isLoading} />}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- primitives -- */

type Tone = "neutral" | "warning" | "danger";

const TONE_TEXT: Record<Tone, string> = {
  neutral: "text-ink",
  warning: "text-warning",
  danger: "text-danger",
};

/**
 * Le squelette d'une carte de module : un titre cliquable, des nombres, du
 * détail, et les actions en bas.
 *
 * Les actions sont toujours au même endroit dans les sept cartes : sur un
 * téléphone, la place du bouton compte plus que son libellé.
 */
function ModuleCard({
  title,
  icon: Icon,
  to,
  children,
  actions,
}: {
  title: string;
  icon: LucideIcon;
  to: string;
  children: ReactNode;
  actions: ReactNode;
}) {
  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader
        className="shrink-0"
        title={
          <span className="flex items-center gap-2">
            <Icon className="size-4 shrink-0 text-ink-muted" aria-hidden />
            {title}
          </span>
        }
        action={
          <Link
            to={to}
            className="shrink-0 text-[12px] text-ink-muted hover:text-ink"
          >
            Ouvrir
          </Link>
        }
      />
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">{children}</div>
      <div className="flex flex-wrap gap-2 border-t border-border/60 p-3">{actions}</div>
    </Card>
  );
}

/** Un nombre et ce qu'il compte. Le nombre d'abord : c'est ce qu'on lit. */
function Stat({
  label,
  value,
  tone = "neutral",
  loading,
}: {
  label: string;
  value: number | null;
  tone?: Tone;
  loading?: boolean;
}) {
  return (
    <div className="min-w-0 flex-1 basis-[calc(50%-0.375rem)]">
      {loading || value === null ? (
        <Skeleton className="h-6 w-12" />
      ) : (
        <span className={cn("tnum block text-xl font-semibold leading-none", TONE_TEXT[tone])}>
          {formatInteger(value)}
        </span>
      )}
      <span className="mt-1 block text-[11px] leading-tight text-ink-subtle">{label}</span>
    </div>
  );
}

function Stats({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-3">{children}</div>;
}

/** Une ligne de détail : ce qu'on reconnaît, et ce qui reste à en faire. */
function MiniRow({
  to,
  primary,
  secondary,
  trailing,
}: {
  to: string;
  primary: string;
  secondary?: string;
  trailing?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-hover"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-ink">{primary}</span>
        {secondary && (
          <span className="block truncate text-[11px] text-ink-subtle">{secondary}</span>
        )}
      </span>
      {trailing}
    </Link>
  );
}

/**
 * Ce qu'on montre quand il n'y a rien.
 *
 * Jamais une carte vide : une phrase qui dit que c'est normal, et l'action qui
 * suit. Le bouton reste dans le pied de la carte — ici on ne répète pas les
 * actions, on explique le vide.
 */
function Nothing({ children }: { children: ReactNode }) {
  return <p className="text-[12px] text-ink-subtle">{children}</p>;
}

/** Une action de carte. `primary` est la seule à porter l'orange. */
function CardAction({
  to,
  label,
  icon: Icon,
  primary,
}: {
  to: string;
  label: string;
  icon?: LucideIcon;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-md px-3 text-[13px] transition-colors",
        primary
          ? "bg-brand font-medium text-ink-inverse hover:bg-brand-hover active:bg-brand-pressed"
          : "border border-border bg-surface-raised text-ink hover:border-border-strong hover:bg-surface-hover",
      )}
    >
      {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
      <span className="truncate">{label}</span>
    </Link>
  );
}

function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: Tone }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase",
        tone === "neutral" && "border-border text-ink-subtle",
        tone === "warning" && "border-warning/40 text-warning",
        tone === "danger" && "border-danger/40 text-danger",
      )}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ cartes -- */

interface CardProps {
  data?: ProcurementOverview;
  loading: boolean;
}

function ListsCard({ data, loading, canWrite }: CardProps & { canWrite: boolean }) {
  const lists = data?.lists;
  return (
    <ModuleCard
      title="Listes"
      icon={ListChecks}
      to="/lists"
      actions={
        <>
          {canWrite && (
            <CardAction to="/lists?new=1" label="Nouvelle liste" icon={Plus} primary />
          )}
          <CardAction to="/lists" label="Voir toutes les listes" />
        </>
      }
    >
      <Stats>
        <Stat label="Listes actives" value={lists?.active ?? null} loading={loading} />
        <Stat label="Brouillons" value={lists?.draft ?? null} loading={loading} />
        <Stat
          label="Lignes à traiter"
          value={lists?.lines_to_get ?? null}
          tone={lists && lists.lines_to_get > 0 ? "warning" : "neutral"}
          loading={loading}
        />
      </Stats>

      {!loading && lists && lists.recent.length === 0 && (
        <Nothing>Aucune liste active. Une liste dit ce que quelqu'un a demandé.</Nothing>
      )}
      {lists && lists.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {lists.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/lists/${row.id}`}
              primary={row.name}
              secondary={`${row.code} · ${formatMaterialListStatus(row.status)} · ${pluralize(row.line_count, "ligne")}`}
              trailing={
                row.to_get_count > 0 ? (
                  <Badge tone="warning">{formatInteger(row.to_get_count)} à traiter</Badge>
                ) : undefined
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}

function EstimatesCard({ data, loading, canWrite }: CardProps & { canWrite: boolean }) {
  const estimates = data?.estimates;
  return (
    <ModuleCard
      title="Estimations"
      icon={Calculator}
      to="/estimates"
      actions={
        <>
          {canWrite && (
            <CardAction to="/estimates?new=1" label="Nouvelle estimation" icon={Plus} primary />
          )}
          <CardAction to="/estimates" label="Voir estimations" />
        </>
      }
    >
      <Stats>
        <Stat label="En cours" value={estimates?.active ?? null} loading={loading} />
        <Stat label="Brouillons" value={estimates?.draft ?? null} loading={loading} />
        <Stat
          label="Versions à compléter"
          value={estimates?.versions_in_draft ?? null}
          tone={estimates && estimates.versions_in_draft > 0 ? "warning" : "neutral"}
          loading={loading}
        />
      </Stats>

      {!loading && estimates && estimates.recent.length === 0 && (
        <Nothing>Aucune estimation. Une estimation chiffre un travail avant de l'engager.</Nothing>
      )}
      {estimates && estimates.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {estimates.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/estimates/${row.id}`}
              primary={row.name}
              secondary={
                row.current_version_number === null
                  ? row.code
                  : `${row.code} · V${row.current_version_number}`
              }
              trailing={
                row.current_version_status ? (
                  <Badge tone={row.current_version_status === "DRAFT" ? "warning" : "neutral"}>
                    {formatEstimateVersionStatus(row.current_version_status)}
                  </Badge>
                ) : undefined
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}

function RequirementsCard({ data, loading }: CardProps) {
  const needs = data?.requirements;
  const partial = needs ? needs.projects_analysed < needs.projects_open : false;

  return (
    <ModuleCard
      title="Besoins / Manquants"
      icon={PackageSearch}
      to="/projects"
      actions={<CardAction to="/projects" label="Voir besoins" />}
    >
      <Stats>
        <Stat
          label="Chantiers en manque"
          value={needs?.projects_with_shortage ?? null}
          tone={needs && needs.projects_with_shortage > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat
          label="Lignes en manque"
          value={needs?.shortage_lines ?? null}
          tone={needs && needs.shortage_lines > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat
          label="Dont urgentes"
          value={needs?.urgent_shortage_lines ?? null}
          tone={needs && needs.urgent_shortage_lines > 0 ? "danger" : "neutral"}
          loading={loading}
        />
      </Stats>

      {/* Un manque net est faux à la transaction suivante. On dit toujours de
          quand il date, et jamais un montant : ce calcul ignore les prix. */}
      {needs && (
        <p className="text-[11px] text-ink-subtle">
          {needs.projects_open === 0
            ? "Aucun chantier ouvert avec des besoins confirmés."
            : `${pluralize(needs.projects_open, "chantier ouvert", "chantiers ouverts")} · calculé ${formatRelative(needs.calculated_at)}`}
        </p>
      )}
      {partial && needs && (
        <p className="flex items-start gap-1.5 text-[11px] text-warning">
          <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
          {formatInteger(needs.projects_analysed)} chantiers sur{" "}
          {formatInteger(needs.projects_open)} analysés. Ouvrez un chantier pour son calcul
          complet.
        </p>
      )}

      {!loading && needs && needs.projects.length === 0 && needs.projects_open > 0 && (
        <Nothing>Aucun manque net : tout ce qui est confirmé est couvert.</Nothing>
      )}
      {needs && needs.projects.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {needs.projects.map((row) => (
            <MiniRow
              key={row.project_id}
              to={`/projects/${row.project_id}`}
              primary={row.name}
              secondary={`${row.code} · ${pluralize(row.shortage_lines, "ligne en manque", "lignes en manque")}`}
              trailing={
                row.urgent_shortage_lines > 0 ? (
                  <Badge tone="danger">
                    {formatInteger(row.urgent_shortage_lines)} urgent
                  </Badge>
                ) : undefined
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}

/**
 * Analyser écrit des **propositions**, jamais un seuil.
 *
 * C'est pour ça que le bouton peut vivre ici : il ne change aucune valeur en
 * vigueur, il ne commande rien, et le backend redemande le rôle de toute
 * façon. Approuver et appliquer restent sur la page du module, là où on voit
 * ce qu'on approuve.
 */
function ReplenishmentCard({ data, loading }: CardProps) {
  const policies = data?.replenishment;
  const { can } = useAuth();
  const toast = useToast();
  const analyse = useAnalysePolicyBatch();
  // Miroir d'`ANALYSE_ROLES` côté serveur. Masquer un bouton que le serveur
  // refuserait est une politesse, pas une barrière : elle est en base.
  const canAnalyse = can("ADMIN", "MANAGER", "TECHNICIAN");

  async function analyseAll() {
    try {
      const result = await analyse.mutateAsync({ limit: 50 });
      toast.success(
        "Analyse terminée",
        result.created.length > 0
          ? `${pluralize(result.created.length, "suggestion")} à vérifier sur ${result.matched} politique(s).`
          : "Aucun seuil ne demande à changer.",
      );
    } catch (analyseError) {
      toast.error("Analyse impossible", String(analyseError));
    }
  }

  return (
    <ModuleCard
      title="Seuils / Réappro"
      icon={GaugeCircle}
      to="/policies"
      actions={
        <>
          {canAnalyse && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => void analyseAll()}
              loading={analyse.isPending}
            >
              <Sparkles className="size-4" aria-hidden />
              Analyser
            </Button>
          )}
          <CardAction to="/policies" label="Voir seuils" />
        </>
      }
    >
      <Stats>
        <Stat
          label="Recommandations en attente"
          value={policies?.recommendations_pending ?? null}
          tone={policies && policies.recommendations_pending > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat
          label="Périmées"
          value={policies?.recommendations_stale ?? null}
          tone={policies && policies.recommendations_stale > 0 ? "danger" : "neutral"}
          loading={loading}
        />
        <Stat
          label="Données insuffisantes"
          value={policies?.recommendations_without_proposal ?? null}
          loading={loading}
        />
        <Stat
          label="Seuils jamais revus"
          value={policies?.policies_never_reviewed ?? null}
          loading={loading}
        />
      </Stats>

      {!loading && policies && policies.policies === 0 && (
        <Nothing>Aucun seuil défini. Un seuil dit quand racheter, il n'achète rien.</Nothing>
      )}
      {!loading && policies && policies.policies > 0 && policies.recommendations_pending === 0 && (
        <Nothing>
          Aucune recommandation de réapprovisionnement en attente sur{" "}
          {pluralize(policies.policies, "seuil")}.
        </Nothing>
      )}
      {policies && policies.recommendations_stale > 0 && (
        <p className="flex items-start gap-1.5 text-[11px] text-ink-subtle">
          <AlertTriangle className="mt-px size-3.5 shrink-0 text-danger" aria-hidden />
          Une recommandation périmée a été calculée contre un seuil qui a changé depuis. Elle ne
          peut plus s'appliquer telle quelle.
        </p>
      )}
    </ModuleCard>
  );
}

function RfqCard({ data, loading, canWrite }: CardProps & { canWrite: boolean }) {
  const rfqs = data?.rfqs;
  return (
    <ModuleCard
      title="Demandes de prix"
      icon={FileText}
      to="/rfqs"
      actions={
        <>
          {canWrite && <CardAction to="/rfqs?new=1" label="Nouvelle RFQ" icon={Plus} primary />}
          <CardAction to="/rfqs" label="Voir RFQ" />
        </>
      }
    >
      <Stats>
        <Stat label="Brouillons" value={rfqs?.draft ?? null} loading={loading} />
        <Stat
          label="Prêtes à envoyer"
          value={rfqs?.ready_to_send ?? null}
          tone={rfqs && rfqs.ready_to_send > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat label="Annulées" value={rfqs?.cancelled ?? null} loading={loading} />
        <Stat
          label="Sans contact joignable"
          value={rfqs?.ready_without_contact ?? null}
          tone={rfqs && rfqs.ready_without_contact > 0 ? "danger" : "neutral"}
          loading={loading}
        />
      </Stats>

      {/* « Prête à envoyer » ne veut pas dire « envoyée ». Le dire ici évite
          qu'un chiffre orange se lise comme un succès. */}
      {rfqs && rfqs.ready_to_send > 0 && (
        <Nothing>Prête à envoyer veut dire figée et approuvée. Rien n'est encore parti.</Nothing>
      )}
      {!loading && rfqs && rfqs.recent.length === 0 && (
        <Nothing>Aucune demande de prix.</Nothing>
      )}
      {rfqs && rfqs.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {rfqs.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/rfqs/${row.id}`}
              primary={row.title}
              secondary={`${row.code} · ${pluralize(row.supplier_count, "fournisseur")} · ${pluralize(row.line_count, "ligne")}`}
              trailing={
                <Badge tone={row.status === "READY_TO_SEND" ? "warning" : "neutral"}>
                  {formatRfqStatus(row.status)}
                </Badge>
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}

/**
 * Les soumissions fournisseur (V1.6-06).
 *
 * `to_review` est le seul chiffre sur lequel on agit : une soumission reçue ou
 * en vérification attend un humain. Et « validée » ne se lit pas comme une
 * décision d'achat — la carte le dit, parce qu'un chiffre vert invite à le
 * croire.
 */
function QuoteCard({ data, loading }: CardProps) {
  const quotes = data?.quotes;
  return (
    <ModuleCard
      title="Soumissions"
      icon={FileCheck2}
      to="/quotes"
      actions={<CardAction to="/quotes" label="Voir soumissions" />}
    >
      <Stats>
        <Stat
          label="À vérifier"
          value={quotes?.to_review ?? null}
          tone={quotes && quotes.to_review > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat label="Validées" value={quotes?.validated ?? null} loading={loading} />
        <Stat label="Remplacées" value={quotes?.superseded ?? null} loading={loading} />
        <Stat
          label="Lecture impossible"
          value={quotes?.extraction_failed ?? null}
          tone={quotes && quotes.extraction_failed > 0 ? "danger" : "neutral"}
          loading={loading}
        />
      </Stats>

      {quotes && quotes.validated > 0 && (
        <Nothing>
          Validée veut dire vérifiée. Aucun fournisseur n&apos;est choisi pour autant.
        </Nothing>
      )}
      {!loading && quotes && quotes.recent.length === 0 && <Nothing>Aucune soumission.</Nothing>}
      {quotes && quotes.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {quotes.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/quotes/${row.id}`}
              primary={row.supplier_name}
              secondary={`${row.code} · ${pluralize(row.line_count, "ligne")}`}
              // Le vocabulaire de tons de cette section n'a pas de vert :
              // « neutre » pour une soumission validée, « warning » pour ce qui
              // attend encore un œil.
              trailing={
                <Badge tone={row.status === "VALIDATED" ? "neutral" : "warning"}>
                  {formatQuoteStatus(row.status)}
                </Badge>
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}

/**
 * Les décisions d'achat figées.
 *
 * Volontairement sans pastille « périmé » : ce compteur coûterait une
 * comparaison par plan. Ce que la carte dit à la place, c'est qu'un plan
 * approuvé n'a **rien commandé** — c'est la confusion la plus probable devant
 * le mot « approuvé », et elle se corrige ici en une phrase.
 */
function PlanCard({ data, loading }: CardProps) {
  const plans = data?.plans;
  return (
    <ModuleCard
      title="Plans d'achat"
      icon={ClipboardCheck}
      to="/purchase-plans"
      actions={<CardAction to="/purchase-plans" label="Voir les plans" />}
    >
      <Stats>
        <Stat
          label="À approuver"
          value={plans?.to_approve ?? null}
          tone={plans && plans.to_approve > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat label="Approuvés" value={plans?.approved ?? null} loading={loading} />
        <Stat label="Remplacés" value={plans?.superseded ?? null} loading={loading} />
        <Stat label="Annulés" value={plans?.cancelled ?? null} loading={loading} />
      </Stats>

      {plans && plans.approved > 0 && (
        <Nothing>
          Approuvé veut dire décidé. Rien n&apos;a été commandé pour autant.
        </Nothing>
      )}
      {!loading && plans && plans.recent.length === 0 && (
        <Nothing>Aucun plan. Ils se figent depuis la comparaison d&apos;une demande de prix.</Nothing>
      )}
      {plans && plans.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {plans.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/purchase-plans/${row.id}`}
              primary={row.rfq_code ?? "Demande retirée"}
              secondary={`${row.revision > 1 ? `${row.code} rév. ${row.revision}` : row.code} · ${pluralize(row.line_count, "ligne")}`}
              trailing={
                <Badge tone={row.status === "DRAFT" ? "warning" : "neutral"}>
                  {formatPlanStatus(row.status)}
                </Badge>
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}

/**
 * Les commandes issues d'un plan.
 *
 * Trois chiffres, trois gestes différents — et le troisième est le seul qui
 * rende l'inventaire faux tant qu'il dure : de la marchandise comptée sur le
 * quai, pas encore entrée en stock. Il est en avertissement dès un, parce que
 * quelqu'un doit aller finir.
 */
function OrderCard({ data, loading }: CardProps) {
  const orders = data?.orders;
  return (
    <ModuleCard
      title="Commandes"
      icon={Truck}
      to="/commandes"
      actions={<CardAction to="/commandes" label="Voir les commandes" />}
    >
      <Stats>
        <Stat
          label="À passer"
          value={orders?.to_send ?? null}
          tone={orders && orders.to_send > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat label="En attente" value={orders?.to_receive ?? null} loading={loading} />
        <Stat
          label="À finaliser"
          value={orders?.drafts_to_finalize ?? null}
          tone={orders && orders.drafts_to_finalize > 0 ? "danger" : "neutral"}
          loading={loading}
        />
        <Stat label="Annulées" value={orders?.cancelled ?? null} loading={loading} />
      </Stats>

      {orders && orders.drafts_to_finalize > 0 && (
        <Nothing>
          De la marchandise est comptée mais pas encore en stock. Finalisez pour qu&apos;elle y
          entre.
        </Nothing>
      )}
      {!loading && orders && orders.recent.length === 0 && (
        <Nothing>
          Aucune commande. Elles se créent depuis la fiche d&apos;un plan d&apos;achat approuvé.
        </Nothing>
      )}
      {orders && orders.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {orders.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/commandes/${row.id}`}
              primary={row.supplier_name || row.code}
              secondary={`${row.code} · ${pluralize(row.line_count, "ligne")}${
                row.expected_on ? ` · attendu ${formatDate(row.expected_on)}` : ""
              }`}
              trailing={
                <Badge tone={row.status === "DRAFT" ? "warning" : "neutral"}>
                  {formatPurchaseOrderStatus(row.status)}
                </Badge>
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}


/**
 * Ce qui repart, et ce à quoi on a renoncé (V1.6-10A).
 *
 * Un seul chiffre demande un geste : les brouillons. Un retour préparé et
 * jamais envoyé est de la marchandise que quelqu'un croit partie et qui dort
 * encore sur une tablette — l'inventaire est juste, l'intention ne l'est plus.
 *
 * Les renoncements récents sont une **liste**, pas un compteur : « 60
 * abandonnés » ne dit ni qui, ni pourquoi, et c'est justement ce qu'on vient
 * chercher ici.
 */
function ReturnCard({ data, loading }: CardProps) {
  const returns = data?.returns;
  return (
    <ModuleCard
      title="Retours fournisseur"
      icon={Undo2}
      to="/retours-fournisseur"
      actions={<CardAction to="/retours-fournisseur" label="Voir les retours" />}
    >
      <Stats>
        <Stat
          label="À envoyer"
          value={returns?.drafts ?? null}
          tone={returns && returns.drafts > 0 ? "warning" : "neutral"}
          loading={loading}
        />
      </Stats>

      {returns && returns.drafts > 0 && (
        <Nothing>
          Des retours sont préparés mais rien n&apos;est sorti du stock. Finalisez-les quand le
          colis part.
        </Nothing>
      )}
      {!loading && returns && returns.recent.length === 0 && (
        <Nothing>
          Aucun retour. Ils se préparent depuis la fiche d&apos;une commande, sur ce qui a
          réellement été reçu.
        </Nothing>
      )}
      {returns && returns.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {returns.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/retours-fournisseur/${row.id}`}
              primary={row.supplier_name || row.code}
              secondary={`${row.code} · ${pluralize(row.line_count, "article")}`}
              trailing={
                <Badge tone={row.status === "DRAFT" ? "warning" : "neutral"}>
                  {formatSupplierReturnStatus(row.status)}
                </Badge>
              }
            />
          ))}
        </div>
      )}

      {returns && returns.recent_remainder_actions.length > 0 && (
        <div className="mt-2 border-t border-border/60 pt-2">
          <p className="text-[11px] font-medium text-ink-muted">Reliquats abandonnés</p>
          <div className="-mx-2 flex flex-col">
            {returns.recent_remainder_actions.map((row) => (
              <MiniRow
                key={row.id}
                to={`/commandes/${row.purchase_order_id}`}
                primary={row.description ?? row.purchase_order_code}
                secondary={`${row.purchase_order_code} · ${formatQuantityWithUnit(
                  row.quantity,
                  row.unit,
                )} · ${formatRemainderReason(row.reason_code)}`}
              />
            ))}
          </div>
        </div>
      )}
    </ModuleCard>
  );
}

/**
 * Ce qui reste sur les chantiers (V1.6-10B).
 *
 * Trois nombres, et le troisième est le seul qui trompe : une décision prise et
 * jamais exécutée fait croire que la marchandise est repartie. Elle est encore
 * là, et le stock ne le sait pas.
 */
function SurplusCard({ data, loading }: CardProps) {
  const surpluses = data?.surpluses;
  return (
    <ModuleCard
      title="Surplus de projet"
      icon={PackageOpen}
      to="/surplus"
      actions={<CardAction to="/surplus" label="Voir les surplus" />}
    >
      <Stats>
        <Stat
          label="À décider"
          value={surpluses?.open_declarations ?? null}
          tone={surpluses && surpluses.open_declarations > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat
          label="Entamés"
          value={surpluses?.partially_disposed ?? null}
          loading={loading}
        />
        <Stat
          label="Décisions non exécutées"
          value={surpluses?.dispositions_pending ?? null}
          tone={surpluses && surpluses.dispositions_pending > 0 ? "warning" : "neutral"}
          loading={loading}
        />
      </Stats>

      {surpluses && surpluses.dispositions_pending > 0 && (
        <Nothing>
          Des décisions sont prises mais rien n&apos;a bougé. La marchandise est encore sur le
          chantier tant qu&apos;elles ne sont pas exécutées.
        </Nothing>
      )}
      {!loading && surpluses && surpluses.recent.length === 0 && (
        <Nothing>
          Aucun surplus déclaré. Une déclaration naît sur le terrain, sur ce qui reste
          réellement à un endroit.
        </Nothing>
      )}
      {surpluses && surpluses.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {surpluses.recent.map((row) => (
            <MiniRow
              key={row.id}
              to={`/surplus/${row.id}`}
              primary={row.project_name || row.code}
              secondary={`${row.code} · ${pluralize(row.line_count, "article")}`}
              trailing={
                <Badge tone={row.status === "OPEN" ? "warning" : "neutral"}>
                  {formatSurplusStatus(row.status)}
                </Badge>
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}

function OutboundCard({ data, loading }: CardProps) {
  const outbound = data?.outbound;
  const stuck = outbound ? outbound.failed + outbound.unknown_delivery : 0;

  return (
    <ModuleCard
      title="Messages sortants"
      icon={Send}
      to="/messages/outgoing"
      actions={<CardAction to="/messages/outgoing" label="Voir messages sortants" />}
    >
      <Stats>
        <Stat
          label="En file"
          value={outbound?.queued ?? null}
          tone={outbound && outbound.queued > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat
          label="Échecs"
          value={outbound?.failed ?? null}
          tone={outbound && outbound.failed > 0 ? "danger" : "neutral"}
          loading={loading}
        />
        <Stat
          label="Livraison inconnue"
          value={outbound?.unknown_delivery ?? null}
          tone={outbound && outbound.unknown_delivery > 0 ? "danger" : "neutral"}
          loading={loading}
        />
        <Stat label="Envoyés (7 j)" value={outbound?.sent_recently ?? null} loading={loading} />
      </Stats>

      {/* La distinction que ce module existe pour tenir : ni « envoyé », ni
          « échoué ». Le message est peut-être arrivé, et renvoyer pourrait
          faire un doublon chez le fournisseur. */}
      {outbound && outbound.unknown_delivery > 0 && (
        <p className="flex items-start gap-1.5 text-[11px] text-ink-subtle">
          <AlertTriangle className="mt-px size-3.5 shrink-0 text-danger" aria-hidden />
          Livraison inconnue n'est pas un envoi. Le fournisseur l'a peut-être reçu : vérifiez avant
          de relancer.
        </p>
      )}
      {!loading && outbound && stuck === 0 && outbound.queued === 0 && (
        <Nothing>Rien en attente ni en échec.</Nothing>
      )}
    </ModuleCard>
  );
}

function InboundCard({ data, loading }: CardProps) {
  const inbound = data?.inbound;
  return (
    <ModuleCard
      title="Messages reçus"
      icon={Inbox}
      to="/messages/incoming"
      actions={<CardAction to="/messages/incoming" label="Voir messages reçus" />}
    >
      <Stats>
        <Stat
          label="Non classifiés"
          value={inbound?.unclassified ?? null}
          tone={inbound && inbound.unclassified > 0 ? "warning" : "neutral"}
          loading={loading}
        />
        <Stat label="Liés à une RFQ" value={inbound?.linked_to_rfq ?? null} loading={loading} />
        <Stat label="Reçus au total" value={inbound?.total ?? null} loading={loading} />
      </Stats>

      {!loading && inbound && inbound.total === 0 && (
        <Nothing>Aucun message reçu.</Nothing>
      )}
      {inbound && inbound.recent.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {inbound.recent.map((row) => (
            <MiniRow
              key={row.id}
              to="/messages/incoming"
              primary={row.subject || row.from_email}
              secondary={`${row.from_email} · ${formatRelative(row.received_at)}`}
              trailing={
                row.rfq_code ? (
                  <Badge>{row.rfq_code}</Badge>
                ) : row.classified_as === "UNCLASSIFIED" ? (
                  <Badge tone="warning">à classer</Badge>
                ) : undefined
              }
            />
          ))}
        </div>
      )}
    </ModuleCard>
  );
}
